/**
 * v0.5 Stage D — "Why am I being asked?" copy library.
 * v0.6 Stage F.2 — copy refinement: every constant audited against four
 * rules pinned by `whyAsking.test.ts::WHY_ASKING_COPY contract`:
 *   1. Lead with the *evidence* the agent saw, not the action it wants.
 *   2. Bullet-list when there are 2+ distinct signals (rare here; most
 *      copies have a single evidence signal followed by action options).
 *   3. Cap at ≤200 chars (single-line rendering, leaves headroom for
 *      narrower viewports).
 *   4. Always end with the question the reviewer is answering — the
 *      contract test asserts every constant ends with `?`.
 *
 * Each HITL card surfaces a short plain-English explanation of why
 * the system needs reviewer input. The copy is contextualised per
 * card type so the reviewer doesn't have to guess what's ambiguous —
 * date-format-ambiguous reads differently from mixed-content-unclear.
 *
 * Single dispatch entry point (`whyAsking`) branches on the HITL's
 * shape (resolution_type / agent_id / options / tier) and returns the
 * matching string. Returns a sensible generic fallback when no
 * specific branch matches so the surface is never empty.
 *
 * Pure functions; no React imports, no DOM. Tested in isolation.
 *
 * Adding a new card type
 * ----------------------
 * 1. Add a new `case` branch in {@link whyAsking} matching on the
 *    discriminator that uniquely identifies the card.
 * 2. Add the new `COPY_X` constant + entry in `WHY_ASKING_COPY` —
 *    the v0.6 F.2 contract test iterates this map, so new entries
 *    are auto-covered by the four-rule pin.
 * 3. Add a vitest dispatch case in `whyAsking.test.ts` so the
 *    branch order is locked.
 *
 * Single-discriminator branches (e.g. `pii_treatment`) come before
 * heuristic branches (e.g. options-list inspection) so the most
 * specific match wins.
 */
import type { HitlItem } from './types';

const COPY_BOOLEAN_UNCLEAR =
  'Column has more than one way of writing true/false (e.g. `Yes`, `TRUE`, `on`). I can canonicalise these once you confirm — is this a boolean column?';

const COPY_MIXED_CONTENT_UNCLEAR =
  'Column contains multiple kinds of values and no single type fits cleanly. Is this one type I am misreading, or are mixed types legitimate here?';

const COPY_MIXED_TYPES =
  'Column legitimately holds multiple types — each row routes to its detected type’s cleanser, and unmatched rows become anomalies. Which types should I cleanse?';

const COPY_DATE_AMBIGUOUS =
  "Day values are 1–12, so I can't tell DD/MM from MM/DD apart. Which order is right?";

const COPY_DATE_RECOMMENDED =
  'Detected a date pattern in the sample values. Should I enforce the format I picked, use a different one, or leave the column without a format spec?';

const COPY_ENUM_RECOMMENDED =
  'Column has a small set of distinct values. Auto-applying enum would silently flag a 4th value on re-upload as an anomaly. Should I lock these as the category set?';

const COPY_FORMAT_DECISION_RECOMMENDED =
  'Detected a specific value pattern in the sample values. Should I enforce the candidate I picked, use a different pattern, or leave the column without a format spec?';

const COPY_FORMAT_DECISION_UNCLEAR =
  "Multiple plausible patterns matched the column and I can't pick one with confidence. Which is the closest match, or do you have your own?";

const COPY_PII_TREATMENT =
  "PII detected in this column. How should I treat it — mask, hash, drop, or pass through (if it's part of a CPA-confirmed audit field)?";

const COPY_CROSS_FIELD_RULE =
  'Detected a relationship between columns that looks like a rule (e.g. `ship_date >= order_date`). Should I enforce it, modify the operator, or reject the proposal?';

const COPY_TABLE_BOUNDARY =
  'Detected a possible table-boundary issue (multiple tables, or trailing/leading rows that may not belong). Should I split into separate tables or treat as one?';

const COPY_UNIT_CONVERSION =
  'Column has measurements in multiple units (e.g. some rows in kg, others in lb). Should I convert every row to a target unit, or keep the original units?';

const COPY_JSON_FLATTEN =
  'Column contains JSON values that would expand into more than 20 columns if fully flattened. Should I flatten top-level keys only, all nested keys (depth ≤ 5), or keep the JSON string?';

const COPY_SERIES_CADENCE =
  'Column looks like a uniform-cadence date series (e.g. daily, weekly, monthly observations). Should I fill missing dates by interpolating the paired numeric columns, or keep rows as-is?';

const COPY_ADDRESS_FORMAT =
  'Column has freeform postal addresses; the parser extracted structured components (house, road, city, postcode, …) on under 80% of rows. Should I replace, keep both, or keep the freeform text?';

const COPY_GENERIC_FALLBACK =
  "I'm not sure how to resolve this column from the sample values alone. Which resolution from the list fits best?";

function describesMixedContent(issue: string | undefined | null): boolean {
  if (!issue) return false;
  const lower = issue.toLowerCase();
  return (
    lower.includes('mixed') ||
    lower.includes('multiple') ||
    lower.includes('could not be confidently typed')
  );
}

function describesAmbiguousDate(issue: string | undefined | null): boolean {
  if (!issue) return false;
  const lower = issue.toLowerCase();
  return (
    lower.includes('dd/mm') ||
    lower.includes('mm/dd') ||
    lower.includes('ambiguous day')
  );
}

/**
 * Return a contextualised "why am I being asked?" string for a HITL.
 *
 * Branch order is most-specific-first: `resolution_type` discriminator
 * (set explicitly by emitters), then per-resolution heuristics, then
 * a generic fallback. Never returns an empty string.
 */
export function whyAsking(hit: HitlItem): string {
  const tier = hit.hitl_tier ?? null;
  const options = hit.options ?? [];
  const resolutionType = hit.resolution_type ?? null;
  const issue = hit.issue ?? null;

  // ---- Most-specific dispatch on resolution_type --------------------
  if (resolutionType === 'pii_treatment') {
    return COPY_PII_TREATMENT;
  }
  if (resolutionType === 'cross_field_rule') {
    return COPY_CROSS_FIELD_RULE;
  }
  if (resolutionType === 'unit_conversion') {
    return COPY_UNIT_CONVERSION;
  }
  if (resolutionType === 'json_flatten') {
    return COPY_JSON_FLATTEN;
  }
  if (resolutionType === 'series_cadence') {
    return COPY_SERIES_CADENCE;
  }
  if (resolutionType === 'address_format') {
    return COPY_ADDRESS_FORMAT;
  }
  if (resolutionType === 'table_boundary') {
    return COPY_TABLE_BOUNDARY;
  }
  if (resolutionType === 'format_decision') {
    if (tier === 'recommended') return COPY_FORMAT_DECISION_RECOMMENDED;
    return COPY_FORMAT_DECISION_UNCLEAR;
  }

  // ---- column_datatype heuristics ----------------------------------
  // The mixed-types option (v0.5 Stage C.2) is the strongest signal —
  // surface its specific copy when present in the options list.
  if (options.includes('mixed_types')) {
    return COPY_MIXED_TYPES;
  }
  if (describesAmbiguousDate(issue)) {
    return COPY_DATE_AMBIGUOUS;
  }
  // Boolean-unclear heuristic: column_datatype + boolean in options +
  // unclear tier.
  if (
    resolutionType === 'column_datatype' &&
    options.includes('boolean') &&
    tier === 'unclear'
  ) {
    return COPY_BOOLEAN_UNCLEAR;
  }
  // Mixed-content fallback: column_datatype + multiple type options +
  // unclear tier (the wildcol-shaped case).
  if (resolutionType === 'column_datatype' && tier === 'unclear') {
    if (describesMixedContent(issue)) return COPY_MIXED_CONTENT_UNCLEAR;
    // Other unclear-tier column_datatype cards still benefit from a
    // type-uncertainty explanation.
    return COPY_MIXED_CONTENT_UNCLEAR;
  }

  // ---- recommended-tier column_datatype ----------------------------
  if (resolutionType === 'column_datatype' && tier === 'recommended') {
    if (issue && issue.toLowerCase().includes('enum')) {
      return COPY_ENUM_RECOMMENDED;
    }
    if (issue && issue.toLowerCase().includes('date')) {
      return COPY_DATE_RECOMMENDED;
    }
    return COPY_FORMAT_DECISION_RECOMMENDED;
  }

  // ---- Generic fallback -------------------------------------------
  return COPY_GENERIC_FALLBACK;
}

// Re-exported constants for tests + downstream consumers that want
// to assert specific copy without re-deriving the dispatch.
export const WHY_ASKING_COPY = {
  BOOLEAN_UNCLEAR: COPY_BOOLEAN_UNCLEAR,
  MIXED_CONTENT_UNCLEAR: COPY_MIXED_CONTENT_UNCLEAR,
  MIXED_TYPES: COPY_MIXED_TYPES,
  DATE_AMBIGUOUS: COPY_DATE_AMBIGUOUS,
  DATE_RECOMMENDED: COPY_DATE_RECOMMENDED,
  ENUM_RECOMMENDED: COPY_ENUM_RECOMMENDED,
  FORMAT_DECISION_RECOMMENDED: COPY_FORMAT_DECISION_RECOMMENDED,
  FORMAT_DECISION_UNCLEAR: COPY_FORMAT_DECISION_UNCLEAR,
  PII_TREATMENT: COPY_PII_TREATMENT,
  CROSS_FIELD_RULE: COPY_CROSS_FIELD_RULE,
  TABLE_BOUNDARY: COPY_TABLE_BOUNDARY,
  UNIT_CONVERSION: COPY_UNIT_CONVERSION,
  JSON_FLATTEN: COPY_JSON_FLATTEN,
  SERIES_CADENCE: COPY_SERIES_CADENCE,
  ADDRESS_FORMAT: COPY_ADDRESS_FORMAT,
  GENERIC_FALLBACK: COPY_GENERIC_FALLBACK,
} as const;
